import { useQuery } from '@tanstack/react-query';
import { integrationsClient } from '@/lib/api/integrations-client';

export interface IntegrationDefinition {
  id: string;
  name: string;
  category_id: string;
  integration_type: string;
  description: string;
  supports_multiple_instances: boolean;
  deprecated: boolean;
}

export interface TenantIntegrationInstance {
  id: string;
  tenant_id: string;
  integration_id: string;
  name: string;
  label?: string;
  deprecated: boolean;
}

export function useIntegrationDefinitions() {
  return useQuery({
    queryKey: ['integrations', 'definitions'],
    queryFn: async () => {
      const response = await integrationsClient.get<{ integrations: IntegrationDefinition[] }>(
        '/integrations',
      );
      return response.data.integrations;
    },
    staleTime: 10 * 60 * 1000,
  });
}

interface AllTenantsResponse {
  integration_instances: TenantIntegrationInstance[];
  total: number;
}

const MAX_PAGES = 50;

export function useAllTenantsIntegrations() {
  return useQuery({
    queryKey: ['integrations', 'all-tenants'],
    queryFn: async () => {
      const allInstances: TenantIntegrationInstance[] = [];
      let offset = 0;
      const limit = 1000;

      for (let page = 0; page < MAX_PAGES; page++) {
        const response = await integrationsClient.get<AllTenantsResponse>(
          '/integration-instances/all-tenants',
          { params: { offset, limit } },
        );
        const items = response.data.integration_instances;
        if (items.length === 0) break;
        allInstances.push(...items);
        if (allInstances.length >= response.data.total) break;
        offset += limit;
      }

      return allInstances;
    },
    staleTime: 2 * 60 * 1000,
  });
}
